import { Component, signal, inject, OnInit, computed } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { ProjectService } from '../../../core/services/project.service';
import { Project, MetaStatus } from '../../../core/models/models';
import { ProjectDetailsComponent } from '../tabs/detail/project-details.component';
import { ProjectDocumentsComponent } from '../tabs/document/project-documents.component';
import { ProjectComptageComponent } from '../tabs/comptage/project-comptage.component';
import { ProjectAffaireComponent } from '../tabs/affaire/project-affaire.component';
import { ProjectSousTraitantComponent } from '../tabs/sous-traitant/project-sous-traitant.component';
import { ProjectTabPlaceholderComponent } from '../tabs/project-tab-placeholder.component';
import { ProjectFatComponent } from '../tabs/fat/project-fat.component';
import { MatIcon } from '@angular/material/icon';
import { AuthService } from '../../../core/services/auth.service';
import { ConfirmDialogComponent } from '../../../shared/components/confirm-dialog.component';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { Router } from '@angular/router';

@Component({
  selector: 'app-project-edit',
  standalone: true,
  imports: [
    CommonModule,
    RouterLink,
    ProjectDetailsComponent,
    ProjectDocumentsComponent,
    ProjectComptageComponent,
    ProjectAffaireComponent,
    ProjectSousTraitantComponent,
    ProjectTabPlaceholderComponent,
    ProjectFatComponent,
    MatIcon,
    MatSnackBarModule,
    ConfirmDialogComponent
  ],
  templateUrl: './project-edit.component.html',
  styleUrl: './project-edit.component.scss'
})
export class ProjectEditComponent implements OnInit {
  private route = inject(ActivatedRoute);
  private projectService = inject(ProjectService);
  private authService = inject(AuthService);
  private snackBar = inject(MatSnackBar);
  private router = inject(Router);

  projectId = signal<number | null>(null);
  project = signal<Project | null>(null);
  activeTab = signal('details');
  isLoading = signal(false);
  errorMessage = signal('');
  successMessage = signal('');

  isAdmin = this.authService.isAdmin;

  isRestrictedRole = computed(() => {
    const role = this.authService.currentUser()?.role;
    return role === 'ROLE_CONTROLEUR' || role === 'ROLE_SOUS_TRAITANT';
  });

  isTabReadOnly = computed(() => {
    const restricted = this.isRestrictedRole();
    const tab = this.activeTab();
    if (!restricted) return false;
    // For restricted roles, these tabs are read-only
    return ['details', 'document', 'comptage', 'affaire', 'sous-traitant'].includes(tab);
  });

  tabs = [
    { id: 'details', label: 'Détails du projet' },
    { id: 'document', label: 'Document' },
    { id: 'comptage', label: 'Comptage' },
    { id: 'affaire', label: 'Affaire' },
    { id: 'sous-traitant', label: 'Sous-traitant' },
    { id: 'fat', label: 'FAT' },
    { id: 'sat', label: 'SAT' }
  ];

  showArchiveDialog = signal(false);
  showUnarchiveDialog = signal(false);

  canArchiveCurrentProject(): boolean {
    const p = this.project();
    return !!p && !p.archived; // Archiver uniquement si non déjà archivé
  }

  canUnarchiveCurrentProject(): boolean {
    const p = this.project();
    return !!p && !!p.archived; // Désarchiver uniquement si archivé
  }

  archiveProject(): void {
    this.showArchiveDialog.set(true);
  }

  confirmArchive(): void {
    const p = this.project();
    if (p && p.id) {
      this.projectService.archiveProject(p.id).subscribe({
        next: (updated) => {
          this.snackBar.open('Projet archivé avec succès', 'Fermer', { duration: 3000 });
          // Mettre à jour l'état local ou revenir à la liste
          this.project.set(updated);
          this.showArchiveDialog.set(false);
          this.router.navigate(['/projects']);
        },
        error: (err) => {
          console.error('Error archiving project:', err);
          this.snackBar.open("Erreur lors de l'archivage du projet", 'Fermer', { duration: 3000 });
        }
      });
    }
  }

  cancelArchive(): void {
    this.showArchiveDialog.set(false);
  }

  // Désarchivage
  unarchiveProject(): void {
    this.showUnarchiveDialog.set(true);
  }

  confirmUnarchive(): void {
    const p = this.project();
    if (p && p.id) {
      this.projectService.unarchiveProject(p.id).subscribe({
        next: (updated) => {
          this.snackBar.open('Projet désarchivé avec succès', 'Fermer', { duration: 3000 });
          this.project.set(updated);
          this.showUnarchiveDialog.set(false);
          // Rester sur la fiche ou revenir à la liste selon le besoin; on reste ici pour revoir l'état
        },
        error: (err) => {
          console.error('Error unarchiving project:', err);
          this.snackBar.open("Erreur lors du désarchivage du projet", 'Fermer', { duration: 3000 });
        }
      });
    }
  }

  cancelUnarchive(): void {
    this.showUnarchiveDialog.set(false);
  }

  ngOnInit(): void {
    const id = this.route.snapshot.paramMap.get('id');
    const tab = this.route.snapshot.queryParamMap.get('tab');
    if (tab) {
      this.activeTab.set(tab);
    }
    if (id) {
      this.projectId.set(+id);
      this.loadProject(+id);
    }
  }

  loadProject(id: number): void {
    this.isLoading.set(true);
    this.projectService.getProjectById(id).subscribe({
      next: (project) => {
        this.project.set(project);
        this.isLoading.set(false);
      },
      error: () => {
        this.errorMessage.set('Erreur lors du chargement du projet');
        this.isLoading.set(false);
      }
    });
  }

  onProjectSaved(): void {
    if (this.projectId()) {
      this.loadProject(this.projectId()!);
    }
  }

  onProjectUpdated(p: Project): void {
    this.project.set(p);
  }

  setActiveTab(tabId: string): void {
    this.activeTab.set(tabId);
  }

  onError(message: string): void {
    this.errorMessage.set(message);
    setTimeout(() => this.errorMessage.set(''), 5000);
  }
}
